import { useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"
import {
  Button,
  Checkbox,
  Col,
  Flex,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from "antd"
import { SearchOutlined } from "@ant-design/icons"
import classNames from "classnames"

import { CurrencyIcon } from "@src/app/components/CurrencyIcon"
import { ConvertCurrency } from "@src/pages/convert/classes/ConvertProcess"
import { capitalizeFirstLetter } from "@src/shared/utils/capitalizeFirstLetter"
import { limitDecimalPlaces } from "@src/shared/utils/limitDecimalPlaces"
import css from "./index.module.css"

type Props = {
  disabled: boolean
  isLoading: boolean
  onSelect: (s: ConvertCurrency) => void
  options: ConvertCurrency[]
  selected?: ConvertCurrency | null
}

export const CurrencySelectField = observer(
  ({ disabled, onSelect, options, selected: selectedProps }: Props) => {
    const [search, setSearch] = useState("")
    const [selected, setSelected] = useState<ConvertCurrency | null>(
      selectedProps ?? null,
    )

    useEffect(() => {
      setSelected(selectedProps ?? null)
    }, [selectedProps])

    const [isOpen, setIsOpen] = useState(false)

    const searched = useMemo(() => {
      return options.filter(
        (item) =>
          item.symbol.toLowerCase().includes(search.toLowerCase()) ||
          item.display_name.toLowerCase().includes(search.toLowerCase()),
      )
    }, [search, options])

    const selectRow = (row: ConvertCurrency) => {
      setSelected(row)
      onSelect(row)
      close()
    }

    const open = () => {
      if (disabled) return

      setIsOpen(true)
    }

    const close = () => {
      setIsOpen(false)
    }

    const getNetwork = (network: string) => {
      if (network == "bank") {
        return "bank transfer"
      }

      return network
    }

    const Currency = ({ item }: { item: ConvertCurrency }) => {
      return (
        <Flex align="center" gap={16}>
          <CurrencyIcon
            network={item.network}
            style={{ height: 46, maxWidth: 56, width: 56 }}
            symbol={item.symbol}
          />
          <div className={css.currencyWrapper}>
            <span className={css.availableSymbol}>
              {["card", "fiat"].includes(item.network)
                ? item.symbol
                : item.symbol_alias || "Disabled"}
              <span
                style={{ paddingLeft: 6 }}
              >{`${item.token_kind ? `(${getNetwork(item.network)})` : ""}`}</span>
            </span>
            {["bank", "card"].includes(item.network) ? (
              capitalizeFirstLetter(getNetwork(item.network))
            ) : (
              <span>
                {item.network !== "fiat" &&
                  `${item.token_kind ? `${item.token_kind}` : `${item.display_name}`}`}
              </span>
            )}
          </div>
        </Flex>
      )
    }

    return (
      <>
        <div
          aria-disabled={disabled}
          className={classNames(css.selected, {
            [`${css.disabled}`]: disabled,
          })}
          onClick={open}
        >
          {selected ? (
            <Currency item={selected}></Currency>
          ) : (
            <Typography.Text style={{ fontSize: 15, fontWeight: 500 }}>
              Select a currency
            </Typography.Text>
          )}
        </div>

        <Modal
          closable
          footer={null}
          onCancel={close}
          open={isOpen}
          title={
            <Typography.Title className={css.title}>
              Select a currency
            </Typography.Title>
          }
          width={620}
        >
          <Flex gap={16} vertical>
            <Input
              allowClear
              onChange={(s) => setSearch(s.target.value)}
              placeholder={"Search by currency"}
              size="large"
              suffix={<SearchOutlined style={{ opacity: 0.5 }} />}
            />

            <ul className={css.list} style={{ padding: "0 6px" }}>
              {searched.map((item) => {
                return (
                  <li
                    className={classNames(css.item, {
                      [`${css.active}`]: item.id === selected?.id,
                    })}
                    key={item.id}
                  >
                    <Row
                      align="middle"
                      justify="space-between"
                      onClick={() => selectRow(item)}
                      style={{ padding: "0 6px" }}
                    >
                      <Col className={css.left} span={12}>
                        <Currency item={item}></Currency>
                      </Col>

                      <Col span={12}>
                        <Flex justify="flex-end" style={{ width: "100%" }}>
                          <Typography.Text
                            style={{
                              fontSize: 16,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typeof item?.balance?.balances?.available ===
                            "string"
                              ? limitDecimalPlaces(
                                  item?.balance?.balances?.available,
                                  item.decimals,
                                )
                              : ""}{" "}
                            {item.symbol}
                          </Typography.Text>
                        </Flex>
                      </Col>
                    </Row>
                  </li>
                )
              })}
            </ul>
          </Flex>
        </Modal>
      </>
    )
  },
)
